import React, { useEffect, useState } from "react";
import { applySortFilter, isEmptyFilter } from "../../utils/tableFunctions";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Grid,
  TextField,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import ListHead from "../table/ListHead";
import Scrollbar from "../table/Scrollbar";
import SearchNotFound from "../table/SearchNotFound";
import MoreMenu from "./MoreMenu";
import {
  getMerchant,
  merchantAction,
  SuspendMerchant,
  getMerchantFilterConditions,
  Log,
  sendDataWithPost,
} from "../../services/services";
import EditDialogs from "./Dialogs/EditDialogs";
import { StyledTableRow } from "../TableRow";
import FormSearch from "../FormSearch";
import CircularLoader from "../circularLoader";
import SearchPrompt from "../SearchPrompt";
import FormDialog from "../FormDialog";
import { pageNumber, rowsPerPage, MAPPED_REGISTERED_COUNTRY } from "../../utils/constants";
import Pagination from "../Pagination";
import Label from "../Label";
import HeaderHelpPage from "../HeaderHelpPage";
import MerchantDetails from "../../pages/details/admin/MerchantDetails";
import CommonDialog from "../CustomDialog";

const defaultValue = {
  is_deleted: false
}

const TABLE_HEAD = [
  { id: "name", label: "Name" },
  { id: "mid", label: "MID" },
  { id: "business_entity", label: "Business Entity" },
  // { id: "oxygen7_entity", label: "Oxygen7 Entity" },
  // { id: "registered_country", label: "Registered Country", align: "center" },
  { id: "is_suspended", label: "Suspended" },
  { id: "is_active", label: "Deleted" },
  { id: "edit" },
];

const ViewMerchant = ({ userRole }) => {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [loaded, setLoaded] = useState(false);

  const [pagination, setPagination] = useState({
    page: pageNumber,
    page_size: rowsPerPage,
  });
  const [tableData, setTableData] = useState({ results: [] });
  const [filter, setFilter] = useState({ is_active: true });
  const [inputValues, setInputValues] = useState({});

  // dialogs
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openSuspendDialog, setOpenSuspendDialog] = useState(false);
  const [openUnsuspendDialog, setOpenUnsuspendDialog] = useState(false);
  const [selectedId, setSelectedId] = useState();
  const [itemId, setItemId] = useState(null);
  // the list is only queried once the user has searched, so the tab opens fast
  const [searched, setSearched] = useState(false);

  const CustomTableCell = ({ align, field, id }) => {
    return (
      <TableCell
        align={align}
        onClick={() => {
          // window.open(`/3/app/merchant-details/${id}/`, "_blank");
          setItemId(id);
        }}
      >
        {field}
      </TableCell>
    );
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const queryPage = (queryParams, params) => {
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `/merchant/merchant/filter/?${queryString}`;
    loaded && setLoaded(false);
    sendDataWithPost(url, params || filter, renderData, setLoaded, () => {
      setPagination(queryParams);
    });
  };

  const handleChangePage = (event, newPage) => {
    queryPage({
      page: newPage + 1,
      page_size: pagination.page_size,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    queryPage({
      page: 1,
      page_size: event.target.value,
    })
  };

  const filteredList = applySortFilter(
    tableData.results || [],
    order,
    orderBy
  );

  const isItemNotFound = tableData.results.length === 0;

  const DeleteMerchantDialog = ({
    open,
    selectedId,
    setResponse,
    setLoaded,
  }) => {
    const {
      handleChange,
      handleSubmit,
      handleBlur,
      values,
      touched,
      errors,
      isSubmitting,
      resetForm
    } = useFormik({
      initialValues: {
        reason: "",
      },
      validationSchema: yup.object().shape({
        reason: yup.string().required("Required").trim(),
      }),
      onSubmit: async (values) => {
        Log.info(`Click button [Submit] - Delete Merchant by Id: ${selectedId}`, values);
        await merchantAction(
          "REMOVE",
          { 
            data: { pk: selectedId }
           },
          null,
          setLoaded,
          values.reason
        );
        handleClose();
      }
    });
  
    const handleClose = () => {
      setOpenDeleteDialog(false);
      resetForm();
    };

    return (
        <FormDialog
          open={open}
          onClose={handleClose}
          title="Delete this Merchant?"
          handleSubmit={handleSubmit}
          loading={isSubmitting}
          maxWidth="sm"
        >
          <Grid container sx={{ alignItems: "center" }} spacing={3}>
            <Grid item md={5} xs={5}>
              <Typography variant="subtitle1">Reason *</Typography>
            </Grid>
            <Grid item md={7} xs={7}>
              <TextField
                name="reason"
                value={values.reason}
                onChange={handleChange}
                onBlur={handleBlur}
                variant="outlined"
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Please enter your reason"
                error={Boolean(touched.reason && errors.reason)}
                helperText={touched.reason && errors.reason}
                disabled={isSubmitting}
              />
            </Grid>
          </Grid>
        </FormDialog>
    );
  };

  const SuspendMerchantDialog = ({
    open,
    selectedId,
    setResponse,
    setLoaded,
  }) => {
    const {
      handleChange,
      handleSubmit,
      handleBlur,
      values,
      touched,
      errors,
      isSubmitting,
      resetForm
    } = useFormik({
      initialValues: {
        reason: "",
      },
      validationSchema: yup.object().shape({
        reason: yup.string().required("Required").trim(),
      }),
      onSubmit: async (values) => {
        Log.info(`Click button [Submit] - Suspend Merchant by Id: ${selectedId}`, values)
        await SuspendMerchant(selectedId, "Suspend", values.reason);
        handleClose();
      }
    });
  
    const handleClose = () => {
      setOpenSuspendDialog(false);
      resetForm();
    };

    return (
        <FormDialog
          open={open}
          onClose={handleClose}
          title="Suspend this Merchant?"
          handleSubmit={handleSubmit}
          loading={isSubmitting}
          maxWidth="sm"
        >
          <Grid container sx={{ alignItems: "center" }} spacing={3}>
            <Grid item md={5} xs={5}>
              <Typography variant="subtitle1">Reason *</Typography>
            </Grid>
            <Grid item md={7} xs={7}>
              <TextField
                name="reason"
                value={values.reason}
                onChange={handleChange}
                onBlur={handleBlur}
                variant="outlined"
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Please enter your reason"
                error={Boolean(touched.reason && errors.reason)}
                helperText={touched.reason && errors.reason}
                disabled={isSubmitting}
              />
            </Grid>
          </Grid>
        </FormDialog>
    );
  };

  const UnsuspendMerchantDialog = ({
    open,
    selectedId,
    setResponse,
    setLoaded,
  }) => {
    const {
      handleChange,
      handleSubmit,
      handleBlur,
      values,
      touched,
      errors,
      isSubmitting,
      resetForm
    } = useFormik({
      initialValues: {
        reason: "",
      },
      validationSchema: yup.object().shape({
        reason: yup.string().required("Required").trim(),
      }),
      onSubmit: async (values) => {
        Log.info(`Click button [Submit] - Unsuspend Merchant by Id: ${selectedId}`, values)
        await SuspendMerchant(selectedId, "Unsuspend", values.reason);
        handleClose();
      }
    });
  
    const handleClose = () => {
      setOpenUnsuspendDialog(false);
      resetForm();
    };

    return (
        <FormDialog
          open={open}
          onClose={handleClose}
          title="Unsuspend this Merchant?"
          handleSubmit={handleSubmit}
          loading={isSubmitting}
          maxWidth="sm"
        >
          <Grid container sx={{ alignItems: "center" }} spacing={3}>
            <Grid item md={5} xs={5}>
              <Typography variant="subtitle1">Reason *</Typography>
            </Grid>
            <Grid item md={7} xs={7}>
              <TextField
                name="reason"
                value={values.reason}
                onChange={handleChange}
                onBlur={handleBlur}
                variant="outlined"
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Please enter your reason"
                error={Boolean(touched.reason && errors.reason)}
                helperText={touched.reason && errors.reason}
                disabled={isSubmitting}
              />
            </Grid>
          </Grid>
        </FormDialog>
    );
  };

  const renderData = res => {
    const newData = res.results.map(item => ({
      ...item,
      business_entity: item.business_entity?.business_name,
      oxygen7_entity: item.oxygen7_entity?.entity,
      // registered_country: item.registered_country?.registered_country
    }))
    
    setTableData({
      ...res,
      results: newData
    });
  }

  const [merchantFilter, setMerchantFilter] = useState({});
  const [fLoaded, setFLoaded] = useState(false);

  useEffect(() => {
    getMerchantFilterConditions(setMerchantFilter, setFLoaded);
  }, []);

  const handleCloseDialog = () => {
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
  };

  // filter
  const onFilterSubmit = (params) => {
    // searching with an empty form falls back to the filter the tab used to
    // load with on first render
    const fParams = isEmptyFilter(params) ? { ...defaultValue } : params;
    const newPrams = {
      ...fParams,
      business_entity: fParams.business_entity?.id,
      oxygen7_entity: fParams.oxygen7_entity?.id,
      name: fParams.name?.id,
      is_active: (fParams.is_deleted === "all" ? undefined : !fParams.is_deleted),
      name_input: inputValues?.name?.trim() || null,
      mid_input: inputValues?.mid?.trim() || null,
      business_entity_input: inputValues?.business_entity?.trim() || null
    }
    const newPagination = {
      ...pagination,
      page: 1,
    };

    if (newPrams.is_active === 'is_suspended') delete newPrams.is_suspended;
    if (newPrams.is_active === 'all') delete newPrams.is_active;
    Log.info('Click button [Apply Filter] - Search Merchant list', newPrams);

    setSearched(true);
    setFilter(newPrams);
    queryPage(newPagination, newPrams);
  };

  const schema = [
    {
      type: "search-select",
      label: "Name",
      param: "name",
      options: merchantFilter?.name?.map((name) => ({
        label: name?.toUpperCase(),
        id: name,
      })),
      inputValues,
      setInputValues,
    },
    {
      type: "search-select",
      label: "MID",
      param: "mid",
      options: merchantFilter?.mid?.map(item => item),
      inputValues,
      setInputValues,
    },
    {
      type: "search-select",
      label: "Business Entity",
      param: "business_entity",
      options: merchantFilter?.business_entity?.map((entity) => ({
        label: entity.business_entity__business_name?.toUpperCase(),
        id: entity.business_entity__id,
      })),
      inputValues,
      setInputValues,
    },
    // {
    //   type: "search-select",
    //   label: "Oxygen7 Entity",
    //   param: "oxygen7_entity",
    //   options: (merchantFilter?.oxygen7_entity || []).map((entity) => ({
    //     label: entity.entity?.toUpperCase(),
    //     id: entity.id,
    //   })),
    //   inputValues,
    //   setInputValues,
    // },
    
    // {
    //   type: "select",
    //   label: "Registered Country",
    //   param: "registered_country",
    //   options: merchantFilter?.registered_country?.map((country) => ({
    //     id: country.registered_country__id,
    //     name: MAPPED_REGISTERED_COUNTRY[country.registered_country__registered_country],
    //   }))
    // },
    {
      type: "select",
      label: "Suspended",
      param: "is_suspended",
      options: [
        { name: "All", id: "all" },
        { name: "Yes", id: true },
        { name: "No", id: false },
      ]
    },
    {
      type: "select",
      label: "Deleted",
      param: "is_deleted",
      options: [
        { name: "All", id: "all" },
        { name: "Yes", id: true },
        { name: "No", id: false },
      ]
    },
  ]

  return (
    <Box>
      <FormSearch
        schema={schema}
        loading={!fLoaded}
        onSearch={onFilterSubmit}
        defaultValue={defaultValue}
      />
      {!searched ?
        <SearchPrompt />
        : loaded ?
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  onRequestSort={handleRequestSort}
                  checkbox={false}
                />
                <TableBody>
                  {filteredList.map((row) => {
                        const {
                          id,
                          name,
                          mid,
                          business_entity,
                          is_active,
                          is_suspended,
                        } = row;

                        return (
                          <StyledTableRow
                            hover
                            key={id}
                          >
                            <CustomTableCell
                              field={name ? name.toUpperCase() : ""}
                              id={id}
                            />
                            <CustomTableCell field={mid} id={id} />
                            <CustomTableCell
                              field={
                                business_entity
                                  ? business_entity.toUpperCase()
                                  : ""
                              }
                              id={id}
                            />
                            {/* <CustomTableCell
                              field={
                                oxygen7_entity
                                  ? oxygen7_entity.toUpperCase()
                                  : ""
                              }
                              id={id}
                            /> */}
                            {/* <CustomTableCell
                              align="center"
                              field={
                                registered_country
                                  ? MAPPED_REGISTERED_COUNTRY[registered_country]
                                  : ""
                              }
                              id={id}
                            />  */}
                            <CustomTableCell
                              field={
                                <Label color={is_suspended ? "error" : "success"}>
                                  {is_suspended ? "YES" : "NO"}
                                </Label>
                              }
                              id={id}
                            />
                            <CustomTableCell
                              field={(
                                <Label color={!is_active ? "error" : "success"}>
                                  {!is_active ? "YES" : "NO"}
                                </Label>
                              )}
                              id={id}
                            />

                            <TableCell align="right">
                              {is_active && (userRole.includes("Edit") ||
                                userRole.includes("Delete")) && (
                                <MoreMenu
                                  setOpenDeleteDialog={setOpenDeleteDialog}
                                  setOpenEditDialog={setOpenEditDialog}
                                  setOpenSuspendDialog={setOpenSuspendDialog}
                                  setOpenUnsuspendDialog={setOpenUnsuspendDialog}
                                  setRow={() => setSelectedId(id)}
                                  userRole={userRole}
                                  is_active={is_active}
                                  is_suspended={is_suspended}
                                />
                              )}
                            </TableCell>
                          </StyledTableRow>
                        );
                      })}
                </TableBody>
                {isItemNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <SearchNotFound />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          {!isItemNotFound && (
            <Pagination
              count={tableData.count}
              pagination={pagination}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Card>
        :
        <CircularLoader />
      }
      {selectedId && (
        <>
          {openEditDialog && (
            <EditDialogs
              open={true}
              onClose={handleCloseDialog}
              selectedId={selectedId}
              setTableLoaded={setLoaded}
            />
          )}
          {openDeleteDialog && (
            <DeleteMerchantDialog
              open={true}
              onClose={handleCloseDialog}
              selectedId={selectedId}
              setLoaded={setLoaded}
            />
          )}
          {openSuspendDialog && (
            <SuspendMerchantDialog
              open={true}
              onClose={handleCloseDialog}
              selectedId={selectedId}
              setLoaded={setLoaded}
            />
          )}
          {openUnsuspendDialog && (
            <UnsuspendMerchantDialog
              open={true}
              onClose={handleCloseDialog}
              selectedId={selectedId}
              setLoaded={setLoaded}
            />
          )}
        </>
      )}

      {itemId && (
        <CommonDialog
          title={<HeaderHelpPage title="Merchant Details" variant="h5" mb={0} />}
          onClose={() => setItemId(null)}
        >
          <MerchantDetails itemId={itemId} />
        </CommonDialog>
      )}
    </Box>
  );
};

export default ViewMerchant;
